import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Database, CheckCircle2, XCircle, Brain } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface TestResult {
  type: string;
  asset?: string;
  success: boolean;
  data?: any;
  error?: string;
  responseTime?: number;
}

export function AIDataHubTester() {
  const [testType, setTestType] = useState<string>("market_overview");
  const [asset, setAsset] = useState<string>("BTC");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<TestResult[]>([]);
  const [aiMessage, setAiMessage] = useState<string>("Como está o BTC hoje? Vale a pena comprar?");

  const testDataHub = async () => {
    setLoading(true);
    const startTime = Date.now();

    try {
      const body: any = { type: testType };
      if (testType === 'asset_data' || testType === 'derivatives' || testType === 'smart_money' || testType === 'news') {
        body.asset = asset;
      }

      console.log('[Data Hub Test] Calling with:', body);

      const { data, error } = await supabase.functions.invoke('ai-data-hub', {
        body
      });

      const responseTime = Date.now() - startTime;

      if (error) {
        console.error('[Data Hub Test] Error:', error);
        const result: TestResult = {
          type: testType,
          asset: body.asset,
          success: false,
          error: error.message,
          responseTime
        };
        setResults(prev => [result, ...prev]);
        toast.error(`Erro: ${error.message}`);
        return;
      }

      console.log('[Data Hub Test] Success:', data);
      const result: TestResult = {
        type: testType,
        asset: body.asset,
        success: true,
        data: data?.data,
        responseTime
      };
      setResults(prev => [result, ...prev]);
      toast.success(`✅ ${testType} retornou dados em ${responseTime}ms`);

    } catch (error: any) {
      const responseTime = Date.now() - startTime;
      console.error('[Data Hub Test] Exception:', error);
      const result: TestResult = {
        type: testType,
        asset,
        success: false,
        error: error.message,
        responseTime
      };
      setResults(prev => [result, ...prev]);
      toast.error(`Erro: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const testAIAnalyst = async (testMessage: string) => {
    setLoading(true);
    const startTime = Date.now();

    try {
      console.log('[AI Analyst Test] Calling with message:', testMessage);

      const { data, error } = await supabase.functions.invoke('ai-analyst-brain', {
        body: { 
          endpoint: 'analyze',
          message: testMessage,
          chat_id: 'test_chat',
          username: 'test_user'
        }
      });

      const responseTime = Date.now() - startTime;

      if (error) {
        console.error('[AI Analyst Test] Error:', error);
        const result: TestResult = {
          type: 'AI Analyst Brain',
          success: false,
          error: error.message,
          responseTime
        };
        setResults(prev => [result, ...prev]);
        toast.error(`Erro no AI Analyst: ${error.message}`);
        return;
      }

      console.log('[AI Analyst Test] Success:', data);
      const result: TestResult = {
        type: 'AI Analyst Brain',
        success: true,
        data: {
          asset: data.asset,
          score: data.score,
          signal: data.signal,
          analysis: data.analysis?.substring(0, 500) + '...' // Preview
        },
        responseTime
      };
      setResults(prev => [result, ...prev]);
      toast.success(`✅ Análise completa! Score: ${data.score}/100 - ${data.signal}`);

    } catch (error: any) {
      const responseTime = Date.now() - startTime;
      console.error('[AI Analyst Test] Exception:', error);
      const result: TestResult = {
        type: 'AI Analyst Brain',
        success: false,
        error: error.message,
        responseTime
      };
      setResults(prev => [result, ...prev]);
      toast.error(`Erro: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const testAllEndpoints = async () => {
    setLoading(true);
    const endpoints = [
      { type: 'market_overview' },
      { type: 'asset_data', asset: 'BTC' },
      { type: 'asset_data', asset: 'ETH' },
      { type: 'valuation', asset: 'BTC' },
      { type: 'derivatives' },
      { type: 'smart_money' },
      { type: 'sentiment' },
      { type: 'news' }
    ];

    for (const endpoint of endpoints) {
      const startTime = Date.now();
      try {
        const { data, error } = await supabase.functions.invoke('ai-data-hub', {
          body: endpoint
        });
        const responseTime = Date.now() - startTime;

        setResults(prev => [{
          type: endpoint.type,
          asset: endpoint.asset,
          success: !error,
          data: data?.data,
          error: error?.message,
          responseTime
        }, ...prev]);

        await new Promise(resolve => setTimeout(resolve, 500)); // Small delay between tests
      } catch (error: any) {
        const responseTime = Date.now() - startTime;
        setResults(prev => [{
          type: endpoint.type,
          asset: endpoint.asset,
          success: false,
          error: error.message,
          responseTime
        }, ...prev]);
      }
    }

    setLoading(false);
    toast.success('Teste completo finalizado!');
  };

  return (
    <div className="space-y-4">
      {/* AI Analyst Test Card */}
      <Card className="border-primary/50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-primary" />
            AI Analyst Brain - Telegram Analyzer
          </CardTitle>
          <CardDescription>
            Teste o endpoint /analyze que simula as mensagens do Telegram
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium">Mensagem (deve conter BTC, ETH, SOL, etc)</label>
            <Textarea
              value={aiMessage}
              onChange={(e) => setAiMessage(e.target.value)}
              placeholder="Ex: Como está o BTC hoje?"
              rows={2}
            />
          </div>
          <Button onClick={() => testAIAnalyst(aiMessage)} disabled={loading || !aiMessage}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Analisando...
              </>
            ) : (
              '🧠 Testar Análise Telegram (como /ask BTC)'
            )}
          </Button>
          
          <div className="flex gap-2 flex-wrap">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => testAIAnalyst("Como está o BTC hoje?")}
              disabled={loading}
            >
              Teste: BTC
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => testAIAnalyst("Vale a pena comprar ETH?")}
              disabled={loading}
            >
              Teste: ETH
            </Button>
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => testAIAnalyst("SOL é uma boa oportunidade?")}
              disabled={loading}
            >
              Teste: SOL
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Data Hub Test Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            AI Data Hub Tester
          </CardTitle>
          <CardDescription>
            Testar endpoints do Data Hub para verificar se retornam dados do banco corretamente
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-4 items-end">
            <div className="flex-1">
              <label className="text-sm font-medium mb-2 block">Tipo de Requisição</label>
              <Select value={testType} onValueChange={setTestType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="market_overview">Market Overview</SelectItem>
                  <SelectItem value="asset_data">Asset Data (específico)</SelectItem>
                  <SelectItem value="valuation">Valuation (P/Fees, FDV/TVL)</SelectItem>
                  <SelectItem value="derivatives">Derivatives</SelectItem>
                  <SelectItem value="smart_money">Smart Money</SelectItem>
                  <SelectItem value="sentiment">Sentiment</SelectItem>
                  <SelectItem value="news">News</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {['asset_data', 'valuation', 'derivatives', 'smart_money', 'news'].includes(testType) && (
              <div className="flex-1">
                <label className="text-sm font-medium mb-2 block">Asset</label>
                <Select value={asset} onValueChange={setAsset}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BTC">BTC</SelectItem>
                    <SelectItem value="ETH">ETH</SelectItem>
                    <SelectItem value="SOL">SOL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <Button onClick={testDataHub} disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Testando...
                </>
              ) : (
                'Testar Endpoint'
              )}
            </Button>

            <Button onClick={testAllEndpoints} disabled={loading} variant="outline">
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Testando...
                </>
              ) : (
                'Testar Todos'
              )}
            </Button>
          </div>

          {results.length > 0 && (
            <Button 
              onClick={() => setResults([])} 
              variant="ghost" 
              size="sm"
            >
              Limpar Resultados
            </Button>
          )}
        </CardContent>
      </Card>

      <div className="space-y-3">
        {results.map((result, index) => (
          <Card key={index} className={result.success ? "border-green-500/50" : "border-red-500/50"}>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  {result.success ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500" />
                  )}
                  <CardTitle className="text-base">
                    {result.type}
                    {result.asset && ` - ${result.asset}`}
                  </CardTitle>
                </div>
                <Badge variant={result.success ? "default" : "destructive"}>
                  {result.responseTime}ms
                </Badge>
              </div>
            </CardHeader>
            <CardContent>
              {result.success ? (
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground">
                    Dados retornados:
                  </div>
                  <pre className="bg-muted p-3 rounded-lg text-xs overflow-auto max-h-96">
                    {JSON.stringify(result.data, null, 2)}
                  </pre>
                  {result.data && (
                    <div className="flex gap-2 flex-wrap mt-2">
                      {Object.keys(result.data).map(key => (
                        <Badge key={key} variant="secondary">
                          {key}: {Array.isArray(result.data[key]) 
                            ? `${result.data[key].length} items`
                            : typeof result.data[key] === 'object' && result.data[key] !== null
                            ? 'object'
                            : 'value'}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-sm text-red-500">
                  Erro: {result.error}
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
